import { parseDateString } from "../../util/date";
import BigNumber from "bignumber.js";
import {
  KlientkontoerklaeringFormValues,
  KlientkontoerklaeringMetadata,
} from "../../interfaces/klientkontoerklaering";
import { KKE_FORM_INITIAL_VALUES } from "../../constants/klientkontoerklaering/initialValues";

export type KlientkontoerklaeringTexts = ReturnType<typeof getTexts>;

export const getTexts = (
  values: KlientkontoerklaeringFormValues,
  metadata: KlientkontoerklaeringMetadata,
) => {
  const { firma, person, slutDato, startDato } = metadata;
  const texts = () => getTexts(values, { ...metadata, slutDato });
  const slutDatoString = parseDateString(new Date(slutDato));

  const startYear = new Date(startDato).getFullYear() + 1;
  const advokatensAdvokatselskabets = (storForbokstav = false) =>
    person && storForbokstav
      ? "Advokatens"
      : person
        ? "advokatens"
        : storForbokstav
          ? "Advokatselskabets"
          : "advokatselskabets";
  const advokatenAdvokatselskabet = (storForbokstav = false) =>
    person && storForbokstav
      ? "Advokaten"
      : person
        ? "advokaten"
        : storForbokstav
          ? "Advokatselskabet"
          : "advokatselskabet";
  const response = {
    // HVIS DER ÆNDRES I INDHOLDET af formTitle SKAL DER MULIGVIS ÆNDRES BACKEND DA formTitle BEHANDLES I BACKEND FOR IS-ERKLÆRINGER!
    // formTitle findes 3 steder i dette projekt!!!
    formTitle: `Erklæring vedr. klienttilsvar m.v. pr. ${slutDatoString} for 
    ${person ? person.navn : ""}${
      firma ? firma.navn + ", CVR-nr. " + firma.cvr : ""
    }`,
    advokatens_section_1: {
      title: `${advokatensAdvokatselskabets(true)} oplysninger`,
      advokatens_section_1_1_samleklientbankkonti: {
        id: "advokatens_section_1_1_samleklientbankkonti",
        title: "Oversigt over samleklientbankkonti",
      },
      advokatens_section_1_2_bogføringssystem: {
        title:
          "Hvilket bogføringssystem er anvendt til klientbogholderiet pr. " +
          slutDatoString +
          "?",
        id: "advokatens_section_1_2_bogføringssystem",
        getAnswer: () =>
          values.advokatens_section_1_2_bogføringssystem
            ? values.advokatens_section_1_2_bogføringssystem
            : "-",
      },
      advokatens_section_1_3_aabningsdag: {
        title: `Dato for første dag advokatvirksomheden havde åben i ${startYear}?`,
        id: "advokatens_section_1_3_aabningsdag",
        getAnswer: () =>
          values.advokatens_section_1_3_aabningsdag
            ? parseDateString(values.advokatens_section_1_3_aabningsdag)
            : "-",
      },
      advokatens_section_1_4_dispensation_ja_nej: {
        title:
          "Er der meddelt dispensation fra kravet om daglig bogføring og regulering?",
        id: "advokatens_section_1_4_dispensation_ja_nej",
        getAnswer: () =>
          values.advokatens_section_1_4_dispensation_ja_nej
            ? values.advokatens_section_1_4_dispensation_ja_nej
            : "-",
      },
      advokatens_section_1_5_dispensationDato: {
        id: "advokatens_section_1_5_dispensationDato",
        label: "Dispensation dateret",
        getValue: () =>
          values.advokatens_section_1_5_dispensationDato
            ? parseDateString(values.advokatens_section_1_5_dispensationDato)
            : "-",
      },
      advokatens_section_1_5_dispensationSagsnr: {
        id: "advokatens_section_1_5_dispensationSagsnr",
        label: "Med sagsnummer",
        getValue: () =>
          values.advokatens_section_1_5_dispensationSagsnr
            ? values.advokatens_section_1_5_dispensationSagsnr
            : "-",
      },
      advokatens_section_1_5_disp_periode: {
        label: "For periode",
        fraId: "advokatens_section_1_5_dispensationPeriodeDatoFra",
        tilId: "advokatens_section_1_5_dispensationPeriodeDatoTil",
        getValue: () => {
          const fraDato =
            values.advokatens_section_1_5_dispensationPeriodeDatoFra
              ? parseDateString(
                  values.advokatens_section_1_5_dispensationPeriodeDatoFra,
                )
              : "";
          const tilDato =
            values.advokatens_section_1_5_dispensationPeriodeDatoTil
              ? parseDateString(
                  values.advokatens_section_1_5_dispensationPeriodeDatoTil,
                )
              : "";
          return `${fraDato} - ${tilDato}`;
        },
        getTilDatoValue: () =>
          values.advokatens_section_1_5_dispensationPeriodeDatoTil
            ? parseDateString(
                values.advokatens_section_1_5_dispensationPeriodeDatoTil,
              )
            : "-",
      },
      advokatens_section_1_6_bogføringAntalDage: {
        title: "Hvor mange dage om ugen skal der bogføres?",
        id: "advokatens_section_1_6_bogføringAntalDage",
        getValue: () => {
          return values.advokatens_section_1_6_bogføringAntalDage
            ? values.advokatens_section_1_6_bogføringAntalDage.toLocaleString(
                "da-DK",
              )
            : "-";
        },
      },
    },
    advokatens_section_2: {
      title: "Afstemning af klienttilsvar",
      subtitle1:
        "Afstemning af klienttilsvar med indestående på klientbankkonti",
      advokatens_section_2_1_samletKlienttilsvar_input: {
        title: `Samlet klienttilsvar af betroede penge i henhold til bogholderiet var pr. ${slutDatoString} ekskl. posteringer foretaget efterfølgende`,
        inputId: "advokatens_section_2_1_samletKlienttilsvar_input",
        beloebId: "advokatens_section_2_1_samletKlienttilsvar_beloeb",
        getValue: () =>
          getDisplayCurrency(
            values.advokatens_section_2_1_samletKlienttilsvar_beloeb,
          ),
      },
      advokatens_section_2_2_indestaaende_input: {
        inputId: "advokatens_section_2_2_indestaaende_input",
        beloebId: "advokatens_section_2_2_indestaaende_beloeb",
        getValue: () =>
          getDisplayCurrency(values.advokatens_section_2_2_indestaaende_beloeb),
        title:
          "Pr. " +
          slutDatoString +
          " indestod i henhold til bogholderiet på konti noteret som klientbankkonti, ekskl. posteringer foretaget efterfølgende",
      },
      advokatens_section_2_3_differencen: {
        title: "Differencen",
        getValue: () => {
          return (
            new BigNumber(
              values.advokatens_section_2_1_samletKlienttilsvar_beloeb ?? 0,
            )
              .minus(values.advokatens_section_2_2_indestaaende_beloeb ?? 0)
              .toNumber()
              .toLocaleString("da-DK", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              }) + " kr"
          );
        },
      },
      subtitle2: "Regulering i.h.t. klientkontovedtægtens § 4, stk. 1",
      advokatens_section_2_3_ingen_regulering: {
        id: "advokatens_section_2_3_ingen_regulering",
        getValue: () =>
          values.advokatens_section_2_3_ingen_regulering
            ? "Der er ikke noget at regulere"
            : "-",
      },
      advokatens_section_2_3_forMeget_forLidt: {
        id: "advokatens_section_2_3_forMeget_forLidt",
        getValue: () =>
          values.advokatens_section_2_3_forMeget_forLidt
            ? values.advokatens_section_2_3_forMeget_forLidt === "forMeget"
              ? "For meget indestående"
              : "For lidt indestående"
            : "-",
      },
      advokatens_section_2_3_forMegetellerLidtIndestaaende_input: {
        inputId: "advokatens_section_2_3_forMegetellerLidtIndestaaende_input",
        beloebId: "advokatens_section_2_3_forMegetellerLidtIndestaaende_beloeb",
        getValue: () =>
          getDisplayCurrency(
            values.advokatens_section_2_3_forMegetellerLidtIndestaaende_beloeb,
          ),
      },
      advokatens_section_2_3_haevet_indsat: {
        id: "advokatens_section_2_3_haevet_indsat",
        getValue: () => {
          return values.advokatens_section_2_3_haevet_indsat
            ? values.advokatens_section_2_3_haevet_indsat === "haevet"
              ? "Regulering hævet den "
              : "Regulering indsat den "
            : "-";
        },
      },
      advokatens_section_2_3_haevet_indsat_dato: {
        id: "advokatens_section_2_3_haevet_indsat_dato",
        getValue: () => {
          return values.advokatens_section_2_3_haevet_indsat_dato
            ? parseDateString(values.advokatens_section_2_3_haevet_indsat_dato)
            : "";
        },
      },
    },
    advokatens_section_3: {
      title: "Afstemning af klientbankkonto",
      advokatens_section_3_1_pengeinstitut_indestaende_input: {
        inputId: "advokatens_section_3_1_pengeinstitut_indestaende_input",
        beloebId: "advokatens_section_3_1_pengeinstitut_indestaende_beloeb",
        title:
          "Ifølge kontoudtog fra pengeinstitutter indestod pr. " +
          slutDatoString,
        getValue: () => {
          return getDisplayCurrency(
            values.advokatens_section_3_1_pengeinstitut_indestaende_beloeb,
          );
        },
      },
      advokatens_section_3_2_boghold_indestaende_beloeb: {
        title:
          "Pr. " +
          slutDatoString +
          " indestod i henhold til bogholderiet på konti noteret som klientbankkonti, ekskl. posteringer foretaget efterfølgende",
        getValue: () => {
          return getDisplayCurrency(
            values.advokatens_section_2_2_indestaaende_beloeb,
          );
        },
      },
      advokatens_section_3_3: {
        title: "Differencen, specificeret således:",
        id: "advokatens_section_3_3",
      },
      advokatens_section_3_3_a_input: {
        inputId: "advokatens_section_3_3.advokatens_section_3_3_a_input",
        beloebId: "advokatens_section_3_3.advokatens_section_3_3_a_beloeb",
        title:
          "Hævninger foretaget og bogført, men ikke indløst på opgørelsesdagen",
        getValue: () => {
          return getBeloebTextWithPlusMinus(
            values.advokatens_section_3_3.advokatens_section_3_3_a_beloeb,
            values.advokatens_section_3_3.advokatens_section_3_3_a_plus_minus,
          );
        },
      },
      advokatens_section_3_3_a_plus_minus: {
        id: "advokatens_section_3_3.advokatens_section_3_3_a_plus_minus",
      },
      advokatens_section_3_3_b_input: {
        inputId: "advokatens_section_3_3.advokatens_section_3_3_b_input",
        beloebId: "advokatens_section_3_3.advokatens_section_3_3_b_beloeb",
        title:
          "Renter godskrevet af pengeinstitut før, men bogført efter opgørelsesdagen",
        getValue: () => {
          return getBeloebTextWithPlusMinus(
            values.advokatens_section_3_3.advokatens_section_3_3_b_beloeb,
            values.advokatens_section_3_3.advokatens_section_3_3_b_plus_minus,
          );
        },
      },
      advokatens_section_3_3_b_plus_minus: {
        id: "advokatens_section_3_3.advokatens_section_3_3_b_plus_minus",
      },
      advokatens_section_3_3_c_input: {
        inputId: "advokatens_section_3_3.advokatens_section_3_3_c_input",
        beloebId: "advokatens_section_3_3.advokatens_section_3_3_c_beloeb",
        title:
          "Beløb godskrevet af pengeinstitut før, men bogført efter opgørelsesdagen",
        getValue: () => {
          return getBeloebTextWithPlusMinus(
            values.advokatens_section_3_3.advokatens_section_3_3_c_beloeb,
            values.advokatens_section_3_3.advokatens_section_3_3_c_plus_minus,
          );
        },
      },
      advokatens_section_3_3_c_plus_minus: {
        id: "advokatens_section_3_3.advokatens_section_3_3_c_plus_minus",
      },
      advokatens_section_3_3_d_input: {
        inputId: "advokatens_section_3_3.advokatens_section_3_3_d_input",
        beloebId: "advokatens_section_3_3.advokatens_section_3_3_d_beloeb",
        title:
          "Beløb bogført før, men godskrevet af pengeinstitut efter opgørelsesdagen",
        getValue: () => {
          return getBeloebTextWithPlusMinus(
            values.advokatens_section_3_3.advokatens_section_3_3_d_beloeb,
            values.advokatens_section_3_3.advokatens_section_3_3_d_plus_minus,
          );
        },
      },
      advokatens_section_3_3_d_plus_minus: {
        id: "advokatens_section_3_3.advokatens_section_3_3_d_plus_minus",
      },
      advokatens_section_3_3_e_input: {
        inputId: "advokatens_section_3_3.advokatens_section_3_3_e_input",
        beloebId: "advokatens_section_3_3.advokatens_section_3_3_e_beloeb",
        title: "Andet",
        getValue: () => {
          return getBeloebTextWithPlusMinus(
            values.advokatens_section_3_3.advokatens_section_3_3_e_beloeb,
            values.advokatens_section_3_3.advokatens_section_3_3_e_plus_minus,
          );
        },
      },
      advokatens_section_3_3_e_plus_minus: {
        id: "advokatens_section_3_3.advokatens_section_3_3_e_plus_minus",
      },
      advokatens_section_3_3_e_beskrivelse: {
        id: "advokatens_section_3_3.advokatens_section_3_3_e_beskrivelse",
        getValue: () => {
          return values.advokatens_section_3_3
            .advokatens_section_3_3_e_beskrivelse
            ? values.advokatens_section_3_3.advokatens_section_3_3_e_beskrivelse
            : "-";
        },
      },
    },
    revisors_header:
      "Erklæring om aftalte arbejdshandlinger vedr. klienttilsvar",
    revisors_formaal: {
      header1: `Til ${
        person ? "advokaten" : "advokatselskabet"
      } og Advokatrådet`,
      header2:
        "Formålet med denne erklæring om aftalte arbejdshandlinger og begrænsninger i anvendelse og distribution.",
      p1: `
            Formålet med vores erklæring er udelukkende at assistere 
            Advokatrådet med at vurdere, om ${
              person ? "advokaten" : "advokatselskabet"
            } behandling af betroede midler overholder Advokatsamfundets 
            gældende retningslinjer og bekendtgørelser, og erklæringen 
            kan være uegnet til andet formål.
      `,
      p2: `
            Denne erklæring er alene tiltænkt ${
              person ? "advokaten" : "advokatselskabet"
            } og Advokatrådet og må ikke anvendes af eller distribueres 
            til andre parter.
      `,
    },
    revisors_advokatens_ansvar: {
      header: `${advokatensAdvokatselskabets(true)} ansvar`,
      p1: `
            ${
              person ? "Advokaten" : "Advokatselskabet"
            } har bekræftet, at de aftalte arbejdshandlinger, der er fastlagt af Advokatrådet, er egnede til formålet med opgaven.`,
      p2: `
            ${
              person ? "Advokaten" : "Advokatselskabet"
            } er ansvarlig for det erklæringsemne,
            hvorpå de aftalte arbejdshandlinger udføres.`,
    },
    revisors_revisors_ansvar: {
      header: "Revisors ansvar",
      p1: `
            Vi har udført opgaven om aftalte arbejdshandlinger i 
            overensstemmelse med den internationale standard om beslægtede 
            opgaver ISRS 4400 (Ajourført) Opgaver om aftalte arbejdshandlinger
            og yderligere krav ifølge dansk revisorlovgivning. En opgave om 
            aftalte arbejdshandlinger indebærer, at vi udfører de 
            arbejdshandlinger, der er fastlagt af Advokatrådet, og aftalt 
            med ${advokatenAdvokatselskabet()}, og rapporterer om observationerne, 
            som er de faktiske resultater af de udførte aftalte arbejdshandlinger. 
            Vi udtaler os ikke om egnetheden af de aftalte arbejdshandlinger.`,
      p2: `
            Denne opgave om aftalte arbejdshandlinger er ikke en 
            erklæringsopgave med sikkerhed. Vi afgiver derfor ikke en 
            revisionskonklusion eller udtrykker anden form for 
            konklusion med sikkerhed.`,
      p3: `
            Hvis vi havde udført yderligere arbejdshandlinger,
            kunne andre forhold være fundet og rapporteret.`,
    },
    revisors_prof_etik: {
      header: "Professionel etik og kvalitetsstyring",
      p1: `
            Vi har overholdt relevante bestemmelser i revisorloven og
            International Ethics Standards Board for Acountants’
            internationale retningslinjer for revisorers etiske adfærd
            (IESBA Code), herunder principper om integritet, objektivitet,
            professionel kompetence og fornøden omhu. Vi er ikke underlagt
            uafhængighedskrav ved udførelsen af denne opgave om aftalte
            arbejdshandlinger.`,
      revisors_0_intro_overholder_revisorloven: {
        id: "revisors_0_intro_overholder_revisorloven",
        getText: () => {
          if (
            values.revisors_0_intro_overholder_revisorloven ===
            texts().revisors_prof_etik.revisors_0_intro_overholder_revisorloven
              .overholderIkkeRevisorloven.value
          )
            return texts().revisors_prof_etik
              .revisors_0_intro_overholder_revisorloven
              .overholderIkkeRevisorloven.label;
          if (
            values.revisors_0_intro_overholder_revisorloven ===
            texts().revisors_prof_etik.revisors_0_intro_overholder_revisorloven
              .overholderRevisorloven.value
          )
            return texts().revisors_prof_etik
              .revisors_0_intro_overholder_revisorloven.overholderRevisorloven
              .label;
          return <i>Svar mangler</i>;
        },
        overholderIkkeRevisorloven: {
          value: "overholderIkkeRevisorloven",
          label: `
                Vi skal oplyse, at vi ikke overholder de krav om
                uafhængighed, der gælder for erklæringer med sikkerhed.`,
        },
        overholderRevisorloven: {
          value: "overholderRevisorloven",
          label: `
                Vi kan oplyse, at vi er uafhængige i henhold til
                uafhængighedskravene i IESBA Code.`,
        },
      },
      p2: `
      Vores revisionsfirma anvender International Standard on Quality
      Management 1, ISQM 1, som kræver, at vi designer, implementerer
      og driver et kvalitetsstyringssystem, herunder politikker eller
      procedurer vedrørende overholdelse af etiske krav, faglige
      standarder og gældende lov og øvrig regulering.`,
    },
    revisors_arbhandlinger_og_observationer: {
      header: `Arbejdshandlinger og observationer`,
      textBeforeDatepicker: `
                Vores arbejde har bestået i at udføre de nedenfor beskrevne
                arbejdshandlinger, der er fastlagt af Advokatrådet, og som har
                aftalt med ${advokatenAdvokatselskabet()} i aftalebrev indgået den`,
      datepickerId: "revisors_0_intro_aftalebrev_indgået",
      getDatePickerText: () => {
        return values.revisors_0_intro_aftalebrev_indgået ? (
          parseDateString(values.revisors_0_intro_aftalebrev_indgået)
        ) : (
          <i>dato mangler</i>
        );
      },
      textAfterDatepicker: `
      vedrørende ${advokatensAdvokatselskabets()} behandling af betroede
      midler.`,
    },
    revisors_handling_1: {
      title: "Handling 1",
      fieldset_legend: `
        Vi har sammenholdt de oplysninger om samleklientbankkonti, der
        fremgår af punkt 1.1 i
        ${advokatensAdvokatselskabets()}
        egenerklæring, med årsopgørelse eller anden relevant opgørelse
        pr. ${slutDatoString} fra de pengeinstitutter, der fremgår af
        ${advokatensAdvokatselskabets()} egenerklæring.
      `,
      answer_heading: "Observation",
      revisors_handling_1_question: {
        id: "revisors_handling_1_question",
        getValue: () => {
          return values.revisors_handling_1_question ? (
            (texts().revisors_handling_1.revisors_handling_1_question[
              values.revisors_handling_1_question
            ]?.label ?? <i>Svar ugyldig</i>)
          ) : (
            <p>
              <i>Svar mangler</i>
            </p>
          );
        },
        revisors_handling_1_question_alt_1: {
          id: "revisors_handling_1_question_alt_1",
          label: `
            Vi fandt, at der var overensstemmelse mellem oplysninger 
            om samleklientbankkonti i ${advokatensAdvokatselskabets()} 
            egenerklæring og de oplysninger, der fremgik af opgørelse 
            fra pengeinstitutter pr. ${slutDatoString}.
        `,
        },
        revisors_handling_1_question_alt_2: {
          id: "revisors_handling_1_question_alt_2",
          label: `
          Vi fandt, at der ikke var overensstemmelse mellem oplysninger 
          om samleklientbankkonti i ${advokatensAdvokatselskabets()} egenerklæring 
          og de oplysninger, der fremgik af opgørelse fra pengeinstitutter 
          pr. ${slutDatoString}, idet følgende samleklientbankkonti ikke var nævnt:
        `,
        },
        revisors_handling_1_question_alt_3: {
          id: "revisors_handling_1_question_alt_3",
          label: `
            Vi fandt, at der ikke var overensstemmelse mellem oplysninger om 
            samleklientbankkonti i ${advokatensAdvokatselskabets()} egenerklæring 
            og de oplysninger, der fremgik af opgørelse fra pengeinstitutter 
            pr. ${slutDatoString}, idet følgende bankkonti ikke var samleklientbankkonti:
        `,
        },
        revisors_handling_1_question_alt_4: {
          id: "revisors_handling_1_question_alt_4",
          label: `
            Vi fandt, at der ikke var overensstemmelse mellem oplysninger om 
            samlebankkonti i ${advokatensAdvokatselskabets()} egenerklæring og 
            de oplysninger, der fremgik af opgørelse fra pengeinstitutter pr. 
            ${slutDatoString}. Beskrivelse af uoverensstemmelse:
        `,
        },
      },
      revisors_handling_1_question_alt_2_samleklientbankkonti: {
        id: "revisors_handling_1_question_alt_2_samleklientbankkonti",
      },
      revisors_handling_1_question_alt_3_samleklientbankkonti: {
        id: "revisors_handling_1_question_alt_3_samleklientbankkonti",
      },
      revisors_handling_1_question_alt_4_beskrivelse: {
        id: "revisors_handling_1_question_alt_4_beskrivelse",
      },
      getHandling1Alt2_3_4Text: () => {
        if (
          values.revisors_handling_1_question ===
          "revisors_handling_1_question_alt_2"
        )
          return values.revisors_handling_1_question_alt_2_samleklientbankkonti
            ? values.revisors_handling_1_question_alt_2_samleklientbankkonti
            : "-";
        if (
          values.revisors_handling_1_question ===
          "revisors_handling_1_question_alt_3"
        )
          return values.revisors_handling_1_question_alt_3_samleklientbankkonti
            ? values.revisors_handling_1_question_alt_3_samleklientbankkonti
            : "-";
        if (
          values.revisors_handling_1_question ===
          "revisors_handling_1_question_alt_4"
        )
          return values.revisors_handling_1_question_alt_4_beskrivelse
            ? values.revisors_handling_1_question_alt_4_beskrivelse
            : "-";
        return "";
      },
    },
    revisors_handling_2: {
      title: "Handling 2",
      description: (
        <>
          <p>Vi har udvalgt en stikprøve efter følgende kriterier:</p>
          <ul>
            <li>
              Stikprøven skal omfatte alle bokonti i konkursboer og
              bobestyrerboer
            </li>
            <li>
              Hvis der er mere end 10 bokonti, udvælges i alt 10 bokonti fordelt
              på alle pengeinstitutter, hvori {advokatenAdvokatselskabet()} har
              registreret bokonti.
            </li>
          </ul>
          <p>
            For den udvalgte stikprøve har vi kontrolleret, at bokonti i
            konkursboer og bobestyrerboer i pengeinstitutter var oprettet i
            kurators/bobestyrers/advokatselskabets navn med vedtegning om boets
            navn (separat klientbankkonto).
          </p>
        </>
      ),
      answer_heading: "Observation",
      revisors_handling_2_question: {
        id: "revisors_handling_2_question",
        getAnswer: () => {
          const currentTexts = texts();
          if (!values.revisors_handling_2_question)
            return (
              <p>
                <i>Svar mangler</i>
              </p>
            );
          if (
            values.revisors_handling_2_question ===
            currentTexts.revisors_handling_2.revisors_handling_2_question
              .revisors_handling_2_question_alt_1.id
          )
            return (
              <>
                <p>
                  <span>
                    {
                      currentTexts.revisors_handling_2
                        .revisors_handling_2_alt_1_og_3_antal_bokonti_stikprover
                        .label
                    }
                  </span>
                  <span>
                    <b>
                      {" "}
                      {values.revisors_handling_2_alt_1_og_3_antal_bokonti_stikprover ??
                        "-"}
                    </b>
                  </span>
                </p>
                <p>
                  {
                    currentTexts.revisors_handling_2
                      .revisors_handling_2_question
                      .revisors_handling_2_question_alt_1.label
                  }
                </p>
              </>
            );
          if (
            values.revisors_handling_2_question ===
            currentTexts.revisors_handling_2.revisors_handling_2_question
              .revisors_handling_2_question_alt_2.id
          )
            return (
              <p>
                {" "}
                {
                  currentTexts.revisors_handling_2.revisors_handling_2_question
                    .revisors_handling_2_question_alt_2.label
                }
              </p>
            );
          if (
            values.revisors_handling_2_question ===
            currentTexts.revisors_handling_2.revisors_handling_2_question
              .revisors_handling_2_question_alt_3.id
          )
            return (
              <>
                <p>
                  <span>
                    {
                      currentTexts.revisors_handling_2
                        .revisors_handling_2_alt_1_og_3_antal_bokonti_stikprover
                        .label
                    }
                  </span>
                  <span>
                    <b>
                      {" "}
                      {values.revisors_handling_2_alt_1_og_3_antal_bokonti_stikprover ??
                        "-"}
                    </b>
                  </span>
                </p>
                <p>
                  {
                    currentTexts.revisors_handling_2
                      .revisors_handling_2_question
                      .revisors_handling_2_question_alt_3.labelBeforeNumber
                  }{" "}
                  <b>
                    {values.revisors_handling_2_question_alt_3 ?? (
                      <i>antal mangler</i>
                    )}
                  </b>{" "}
                  {
                    currentTexts.revisors_handling_2
                      .revisors_handling_2_question
                      .revisors_handling_2_question_alt_3.labelAfterNumber
                  }
                </p>

                <dl>
                  <dt>
                    {
                      currentTexts.revisors_handling_2
                        .revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_kurators_bobestyrers_advokatselskabets_navn
                        .label
                    }
                  </dt>
                  <dd>
                    {values.revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_kurators_bobestyrers_advokatselskabets_navn ?? (
                      <i>Antal mangler</i>
                    )}
                  </dd>
                  <dt>
                    {
                      currentTexts.revisors_handling_2
                        .revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_boets_navn
                        .label
                    }
                  </dt>
                  <dd>
                    {values.revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_boets_navn ?? (
                      <i>Antal mangler</i>
                    )}
                  </dd>
                  <dt>
                    {
                      currentTexts.revisors_handling_2
                        .revisors_handling_2_alt_3_antal_bokonti_ikke_separate
                        .label
                    }
                  </dt>
                  <dd>
                    {values.revisors_handling_2_alt_3_antal_bokonti_ikke_separate ?? (
                      <i>Antal mangler</i>
                    )}
                  </dd>
                  <dt>
                    {
                      currentTexts.revisors_handling_2
                        .revisors_handling_2_alt_3_bemaerkning.label
                    }
                  </dt>
                  <dd>
                    {values.revisors_handling_2_alt_3_bemaerkning ?? (
                      <i>Ingen bemærkning</i>
                    )}
                  </dd>
                </dl>
              </>
            );
          return <i>Ugyldig svar</i>;
        },
        revisors_handling_2_question_alt_1: {
          id: "revisors_handling_2_question_alt_1",
          label: `
            Vi fandt, at de udvalgte bokonti i pengeinstituttet var 
            oprettet i kurators/bobestyrers/advokatselskabets navn 
            med vedtegning om boets navn (separat klientbankkonto).
        `,
        },
        revisors_handling_2_question_alt_2: {
          id: "revisors_handling_2_question_alt_2",
          label: `
            ${advokatenAdvokatselskabet(
              true,
            )} behandler ikke boer, og der er derfor ingen bokonti.
        `,
        },
        revisors_handling_2_question_alt_3: {
          id: "revisors_handling_2_question_alt_3",
          labelBeforeNumber: `Vi fandt, at `,
          labelAfterNumber: ` udvalgte bokonti i pengeinstituttet ikke var oprettet i kurators/bobestyrers/advokatselskabets navn med vedtegning om boets navn (separat Klientbankkonto).`,
        },
      },
      revisors_handling_2_alt_1_og_3_antal_bokonti_stikprover: {
        id: "revisors_handling_2_alt_1_og_3_antal_bokonti_stikprover",
        label: "Antal bokonti udvalgt som stikprøve (mellem 1 og 10):",
      },
      revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_kurators_bobestyrers_advokatselskabets_navn:
        {
          id: "revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_kurators_bobestyrers_advokatselskabets_navn",
          label:
            "Antal af de udvalgte bokonti, som ikke var oprettet i kurators/boebestyrers/ advokatselskabets navn:",
        },
      revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_boets_navn: {
        id: "revisors_handling_2_alt_3_antal_bokonti_ikke_opprettet_i_boets_navn",
        label:
          "Antal af de udvalgte bokonti, som ikke var vedtegnet boets navn:",
      },
      revisors_handling_2_alt_3_antal_bokonti_ikke_separate: {
        id: "revisors_handling_2_alt_3_antal_bokonti_ikke_separate",
        label: "Antal af udvalgte bokonti, der ikke var separate konti:",
      },
      revisors_handling_2_alt_3_bemaerkning: {
        id: "revisors_handling_2_alt_3_bemaerkning",
        label: "Bemærkning/forklaring",
        sublabel:
          "(angiv hvilke bokonti, der ikke var oprettet korrekt, og eventuelle bemærkninger hertil)",
      },
    },
    revisors_handling_3: {
      title: "Handling 3",
      description: `
        Vi har sammenholdt oplysningen om bogføringssystem til klientbogholderiet, 
        der fremgår af punkt 1.2 i ${advokatensAdvokatselskabets()} egenerklæring og 
        ${advokatensAdvokatselskabets()} faktiske anvendte bogføringssystem til klientbogholderiet 
        pr. ${slutDatoString}.
      `,
      answer_heading: "Observation",
      revisors_handling_3_question: {
        id: "revisors_handling_3_question",
        revisors_handling_3_question_alt_1: {
          value: "revisors_handling_3_question_alt_1_fandt_overensstemmelse",
          label: `Vi fandt, at der var overensstemmelse mellem ${advokatensAdvokatselskabets()} 
          oplysning om bogføringssystem til klientbogholderiet og det faktisk anvendte 
          bogføringssystem til klientbogholderiet pr. ${slutDatoString}.`,
        },
        revisors_handling_3_question_alt_2: {
          value:
            "revisors_handling_3_question_alt_2_fandt_ikke_overensstemmelse",
          label: `Vi fandt, at der ikke var overensstemmelse mellem ${advokatensAdvokatselskabets()}
          oplysning om bogføringssystem til klientbogholderiet og det faktisk anvendte 
          bogføringssystem til klientbogholderiet pr. ${slutDatoString}, idet følgende bogføringssystem 
          var anvendt:`,
        },
        getAnswer: () => {
          const currentTexts = texts();
          if (!values.revisors_handling_3_question)
            return (
              <p>
                <i>Svar mangler</i>
              </p>
            );
          if (
            values.revisors_handling_3_question ===
            currentTexts.revisors_handling_3.revisors_handling_3_question
              .revisors_handling_3_question_alt_1.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_3
                      .revisors_handling_3_question
                      .revisors_handling_3_question_alt_1.label
                  }
                </p>
              </>
            );
          if (
            values.revisors_handling_3_question ===
            currentTexts.revisors_handling_3.revisors_handling_3_question
              .revisors_handling_3_question_alt_2.value
          )
            return (
              <dl>
                <dt>
                  {
                    currentTexts.revisors_handling_3
                      .revisors_handling_3_question
                      .revisors_handling_3_question_alt_2.label
                  }
                </dt>
                <dd>
                  {values.revisors_handling_3_alt_2_bogfoeringssystem ?? (
                    <i>Bogføringssystem mangler</i>
                  )}
                </dd>
              </dl>
            );
        },
      },
      revisors_handling_3_alt_2_bogfoeringssystem: {
        id: "revisors_handling_3_alt_2_bogfoeringssystem",
      },
    },
    revisors_handling_4: {
      title: "Handling 4",
      description:
        "Vi har kontrolleret, om klientbogholderiet udviser de enkelte mellemværender og det samlede klienttilsvar.",
      answer_heading: "Observation",
      revisors_handling_4_revisionsbesoeg_dato: {
        id: "revisors_handling_4_revisionsbesoeg_dato",
        label: "Dato for revisors besøg:",
        getValue: () => {
          return values.revisors_handling_4_revisionsbesoeg_dato ? (
            parseDateString(values.revisors_handling_4_revisionsbesoeg_dato)
          ) : (
            <i>dato mangler</i>
          );
        },
      },
      revisors_handling_4_question: {
        id: "revisors_handling_4_question",
        getAnswer: () => {
          const currentTexts = texts();
          if (!values.revisors_handling_4_question)
            return (
              <p>
                <i>Svar mangler</i>
              </p>
            );
          if (
            values.revisors_handling_4_question ===
            currentTexts.revisors_handling_4.revisors_handling_4_question
              .revisors_handling_4_question_alt_1.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_4
                      .revisors_handling_4_question
                      .revisors_handling_4_question_alt_1.label
                  }
                </p>
              </>
            );
          if (
            values.revisors_handling_4_question ===
            currentTexts.revisors_handling_4.revisors_handling_4_question
              .revisors_handling_4_question_alt_2.value
          )
            return (
              <dl>
                <dt>
                  {
                    currentTexts.revisors_handling_4
                      .revisors_handling_4_question
                      .revisors_handling_4_question_alt_2.label
                  }
                </dt>
                <dd>
                  {values.revisors_handling_4_alt_2_uddybning ?? (
                    <i>Uddybning mangler</i>
                  )}
                </dd>
              </dl>
            );
        },
        revisors_handling_4_question_alt_1: {
          value: "revisors_handling_4_question_alt_1_fandt_ok",
          label: (
            <>
              Vi fandt den{" "}
              {values.revisors_handling_4_revisionsbesoeg_dato ? (
                parseDateString(values.revisors_handling_4_revisionsbesoeg_dato)
              ) : (
                <i>dd.mm.åååå</i>
              )}
              , at klientbogholderiet udviste de enkelte mellemværender og det
              samlede klienttilsvar.
            </>
          ),
        },
        revisors_handling_4_question_alt_2: {
          value: "revisors_handling_4_question_alt_2_fandt_ikke_ok",
          label: (
            <>
              Vi fandt den{" "}
              {values.revisors_handling_4_revisionsbesoeg_dato ? (
                parseDateString(values.revisors_handling_4_revisionsbesoeg_dato)
              ) : (
                <i>dd.mm.åååå</i>
              )}
              , at klientbogholderiet ikke udviste de enkelte mellemværender
              og/eller det samlede klienttilsvar.
            </>
          ),
        },
      },
      revisors_handling_4_alt_2_uddybning: {
        id: "revisors_handling_4_alt_2_uddybning",
      },
    },
    revisors_handling_5: {
      title: "Handling 5",
      description: `
        Vi har sammenholdt afstemningen af klienttilsvar, 
        der fremgår af punkt 2 i ${advokatensAdvokatselskabets()} 
        egenerklæring, med ${advokatensAdvokatselskabets()}
        bogføring pr. ${slutDatoString}.
      `,
      answer_heading: "Observation",
      revisors_handling_5_question: {
        id: "revisors_handling_5_question",
        revisors_handling_5_question_alt_1: {
          value: "revisors_handling_5_question_alt_1_fandt_ok",
          label: `Vi fandt, at der var overensstemmelse mellem oplysninger 
          i egenerklæringens punkt 2 og ${advokatensAdvokatselskabets()} 
          bogføring pr. ${slutDatoString}.`,
        },
        revisors_handling_5_question_alt_2: {
          value: "revisors_handling_5_question_alt_2_fandt_ikke_ok",
          label: `Vi fandt, at der ikke var overensstemmelse mellem oplysninger 
          i egenerklæringens punkt 2 og ${advokatensAdvokatselskabets()}
          bogføring pr. ${slutDatoString}, jf. punkt 1.2.`,
        },
        getAnswer: () => {
          const currentTexts = texts();
          if (!values.revisors_handling_5_question)
            return (
              <p>
                <i>Svar mangler</i>
              </p>
            );
          if (
            values.revisors_handling_5_question ===
            currentTexts.revisors_handling_5.revisors_handling_5_question
              .revisors_handling_5_question_alt_1.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_5
                      .revisors_handling_5_question
                      .revisors_handling_5_question_alt_1.label
                  }
                </p>
              </>
            );
          if (
            values.revisors_handling_5_question ===
            currentTexts.revisors_handling_5.revisors_handling_5_question
              .revisors_handling_5_question_alt_2.value
          )
            return (
              <dl>
                <dt>
                  {
                    currentTexts.revisors_handling_5
                      .revisors_handling_5_question
                      .revisors_handling_5_question_alt_2.label
                  }
                </dt>
                <dd>
                  {values.revisors_handling_5_alt_2_beskriv_uoverenstemmelsen ??
                    "-"}
                </dd>
              </dl>
            );
        },
      },
      revisors_handling_5_alt_2_beskriv_uoverenstemmelsen: {
        id: "revisors_handling_5_alt_2_beskriv_uoverenstemmelsen",
        label: "Beskriv uoverensstemmelsen",
        sublabel:
          "(hvor stort var beløbet, hvor længe havde uoverensstemmelsen været til stede, og hvad var årsagen)",
      },
    },
    revisors_handling_6: {
      title: "Handling 6",
      description: `
      Vi har kontrolleret, om en eventuel difference i 
      punkt 2 i  ${advokatensAdvokatselskabets()} 
      egenerklæring, blev reguleret førstkommende åbningsdag 
      efter ${slutDatoString}, jf punkt 1.3.
    `,
      answer_heading: "Observation",
      revisors_handling_6_question: {
        id: "revisors_handling_6_question",
        revisors_handling_6_question_alt_1: {
          value: "revisors_handling_6_question_alt_1_fandt_overenstemmelse",
          label: `
          Vi kan bekræfte, at en eventuel difference i punkt 2 i ${advokatensAdvokatselskabets()}  egenerklæring, 
          blev reguleret på førstkommende åbningsdag efter ${slutDatoString}, jf. punkt 1.3.`,
        },
        revisors_handling_6_question_alt_2: {
          value:
            "revisors_handling_6_question_alt_2_fandt_difference_reguleret_efter_foerstkommende_aabningsdag",
          label: `Vi fandt, at differencen i punkt 2 i ${advokatensAdvokatselskabets()} 
          egenerklæring, ikke blev reguleret på førstkommende åbningsdag 
          efter ${slutDatoString}, jf. punkt 1.3.`,
        },
        revisors_handling_6_question_alt_3: {
          value:
            "revisors_handling_6_question_alt_3_fandt_difference_ikke_endnu_reguleret",
          label: `Vi fandt, at differencen i punkt 2 i ${advokatensAdvokatselskabets()} 
          egenerklæring, ikke blev reguleret på førstkommende åbningsdag efter 
          ${slutDatoString}, jf. punkt 1.3., idet differencen endnu ikke er reguleret.`,
        },
        getAnswer: () => {
          const currentTexts = texts();
          if (!values.revisors_handling_6_question)
            return (
              <p>
                <i>Svar mangler</i>
              </p>
            );
          if (
            values.revisors_handling_6_question ===
            currentTexts.revisors_handling_6.revisors_handling_6_question
              .revisors_handling_6_question_alt_1.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_6
                      .revisors_handling_6_question
                      .revisors_handling_6_question_alt_1.label
                  }
                </p>
              </>
            );
          if (
            values.revisors_handling_6_question ===
            currentTexts.revisors_handling_6.revisors_handling_6_question
              .revisors_handling_6_question_alt_2.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_6
                      .revisors_handling_6_question
                      .revisors_handling_6_question_alt_2.label
                  }
                </p>
                <dl>
                  <dt>
                    {
                      currentTexts.revisors_handling_6
                        .revisors_handling_6_alt_2_dato_reguleret.label
                    }
                  </dt>
                  <dd>
                    {values.revisors_handling_6_alt_2_dato_reguleret
                      ? parseDateString(
                          values.revisors_handling_6_alt_2_dato_reguleret,
                        )
                      : "-"}
                  </dd>
                  <dt>
                    {
                      currentTexts.revisors_handling_6
                        .revisors_handling_6_alt_2_forklaring.label
                    }
                  </dt>
                  <dd>{values.revisors_handling_6_alt_2_forklaring ?? "-"}</dd>
                </dl>
              </>
            );
          if (
            values.revisors_handling_6_question ===
            currentTexts.revisors_handling_6.revisors_handling_6_question
              .revisors_handling_6_question_alt_3.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_6
                      .revisors_handling_6_question
                      .revisors_handling_6_question_alt_3.label
                  }
                </p>
                <dl>
                  <dt>
                    {
                      currentTexts.revisors_handling_6
                        .revisors_handling_6_alt_3_uddybning.label
                    }
                  </dt>
                  <dd>{values.revisors_handling_6_alt_3_uddybning ?? "-"}</dd>
                </dl>{" "}
              </>
            );
        },
      },
      revisors_handling_6_alt_2_dato_reguleret: {
        id: "revisors_handling_6_alt_2_dato_reguleret",
        label: "Differencen blev reguleret den:",
      },
      revisors_handling_6_alt_2_forklaring: {
        id: "revisors_handling_6_alt_2_forklaring",
        label: "Forklaring på andet reguleringstidspunkt:",
      },
      revisors_handling_6_alt_3_uddybning: {
        id: "revisors_handling_6_alt_3_uddybning",
        label: "Uddybning:",
      },
    },
    revisors_handling_7: {
      title: "Handling 7",
      description: `
      Vi har sammenholdt afstemningen af klientbankkonti, der fremgår
      af punkt 3 i ${advokatensAdvokatselskabets()} egenerklæring, med
      ${advokatensAdvokatselskabets()} bogholderi og indestående på
      klientbankkonti pr. ${slutDatoString}.
      `,
      answer_heading: "Observation",
      revisors_handling_7_question: {
        id: "revisors_handling_7_question",
        revisors_handling_7_question_alt_1: {
          value: "revisors_handling_7_question_alt_1_fandt_overenstemmelse",
          label: `Vi fandt, at der var overensstemmelse mellem ${advokatensAdvokatselskabets()}
          oplysninger i egenerklæringens punkt 3 og ${advokatensAdvokatselskabets()}
          bogholderi og indestående på klientbankkonti pr. ${slutDatoString}.`,
        },
        revisors_handling_7_question_alt_2: {
          value:
            "revisors_handling_7_question_alt_2_fandt_ikke_overenstemmelse",
          label: `
          Vi fandt, at der ikke var overensstemmelse mellem 
          ${advokatensAdvokatselskabets()} oplysninger i 
          egenerklæringens punkt 3 og ${advokatensAdvokatselskabets()}
          bogholderi og indestående på klientbankkonti 
          pr. ${slutDatoString}.`,
        },
        getAnswer: () => {
          const currentTexts = texts();
          if (!values.revisors_handling_7_question)
            return (
              <p>
                <i>Svar mangler</i>
              </p>
            );
          if (
            values.revisors_handling_7_question ===
            currentTexts.revisors_handling_7.revisors_handling_7_question
              .revisors_handling_7_question_alt_1.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_7
                      .revisors_handling_7_question
                      .revisors_handling_7_question_alt_1.label
                  }
                </p>
              </>
            );
          if (
            values.revisors_handling_7_question ===
            currentTexts.revisors_handling_7.revisors_handling_7_question
              .revisors_handling_7_question_alt_2.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_7
                      .revisors_handling_7_question
                      .revisors_handling_7_question_alt_2.label
                  }
                </p>
                <dl>
                  <dt>
                    {`${currentTexts.revisors_handling_7.revisors_handling_7_alt_2_beskrivelse.label} ${currentTexts.revisors_handling_7.revisors_handling_7_alt_2_beskrivelse.sublabel}:`}
                  </dt>
                  <dd>{values.revisors_handling_7_alt_2_beskrivelse ?? "-"}</dd>
                </dl>
              </>
            );
        },
      },
      revisors_handling_7_alt_2_beskrivelse: {
        id: "revisors_handling_7_alt_2_beskrivelse",
        label: "Beskriv uoverensstemmelsen",
        sublabel:
          "(hvor stort var beløbet, hvor længe havde uoverensstemmelsen været til stede, og hvad var årsagen)",
      },
    },
    revisors_handling_8: {
      title: "Handling 8",
      description: `
        Vi har kontrolleret, om eventuelle afstemningsposter i 
        punkt 3 i ${advokatensAdvokatselskabets()} egenerklæring, 
        var i overensstemmelse med underliggende bilag.
      `,
      answer_heading: "Observation",
      revisors_handling_8_question: {
        id: "revisors_handling_8_question",
        revisors_handling_8_question_alt_1: {
          value: "revisors_handling_8_question_alt_1_fandt_overenstemmelse",
          label: `
            Vi fandt, at eventuelle afstemningsposter i punkt 3 
            i ${advokatensAdvokatselskabets()} egenerklæring, var 
            i overensstemmelse med underliggende bilag.
          `,
        },
        revisors_handling_8_question_alt_2: {
          value:
            "revisors_handling_8_question_alt_2_fandt_ikke_overenstemmelse",
          label: `
            Vi fandt, at eventuelle afstemningsposter i punkt 3 i 
            ${advokatensAdvokatselskabets()} egenerklæring, ikke var i 
            overensstemmelse med underliggende bilag.`,
        },
        getAnswer: () => {
          const currentTexts = texts();
          if (!values.revisors_handling_8_question)
            return (
              <p>
                <i>Svar mangler</i>
              </p>
            );
          if (
            values.revisors_handling_8_question ===
            currentTexts.revisors_handling_8.revisors_handling_8_question
              .revisors_handling_8_question_alt_1.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_8
                      .revisors_handling_8_question
                      .revisors_handling_8_question_alt_1.label
                  }
                </p>
              </>
            );
          if (
            values.revisors_handling_8_question ===
            currentTexts.revisors_handling_8.revisors_handling_8_question
              .revisors_handling_8_question_alt_2.value
          )
            return (
              <>
                <p>
                  {
                    currentTexts.revisors_handling_8
                      .revisors_handling_8_question
                      .revisors_handling_8_question_alt_2.label
                  }
                </p>
                <dl>
                  <dt>
                    {
                      currentTexts.revisors_handling_8
                        .revisors_handling_8_alt_2_beskrivelse.label
                    }
                  </dt>
                  <dd>{values.revisors_handling_8_alt_2_beskrivelse ?? "-"}</dd>
                </dl>
              </>
            );
        },
      },
      revisors_handling_8_alt_2_beskrivelse: {
        id: "revisors_handling_8_alt_2_beskrivelse",
        label: "Uoverensstemmelsens skyldes:",
      },
    },
    revisors_handling_9: {
      title: "Handling 9",
      description: `
      Vi har kontrolleret, om alle bankkonti, som indgår i 
      afstemningen i punkt 3 i ${advokatensAdvokatselskabets()}
      egenerklæring, var betegnet "klientkonto" i 
      pengeinstituttet.
      `,
      answer_heading: "Observation",
      revisors_handling_9_question: {
        id: "revisors_handling_9_question",
        revisors_handling_9_question_alt_1: {
          value: "revisors_handling_9_question_alt_1_fandt_alt_ok",
          label: `
          Vi fandt, at alle ${advokatensAdvokatselskabets()} bankkonti, 
          som indgik i afstemningen i punkt 3 i ${advokatensAdvokatselskabets()}
          egenerklæring, var betegnet "klientkonto" i pengeinstituttet.
          `,
        },
        revisors_handling_9_question_alt_2: {
          value: "revisors_handling_9_question_alt_2_fandt_ikke_alt_ok",
          label: `
            Vi fandt, at ikke alle bankkonti, som indgik i punkt 
            3 i ${advokatensAdvokatselskabets()} egenerklæring, 
            var betegnet "klientkonto".
          `,
        },
      },
      revisors_handling_9_alt_2_beskrivelse: {
        id: "revisors_handling_9_alt_2_beskrivelse",
        label: "Uddybning",
        sublabel:
          "(hvilke konti var ikke betegnet klientkonti og hvordan var de betegnet):",
      },
      getAnswer: () => {
        const currentTexts = texts();
        if (
          values.revisors_handling_9_question ===
          currentTexts.revisors_handling_9.revisors_handling_9_question
            .revisors_handling_9_question_alt_1.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_9.revisors_handling_9_question
                    .revisors_handling_9_question_alt_1.label
                }
              </p>
            </>
          );
        if (
          values.revisors_handling_9_question ===
          currentTexts.revisors_handling_9.revisors_handling_9_question
            .revisors_handling_9_question_alt_2.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_9.revisors_handling_9_question
                    .revisors_handling_9_question_alt_2.label
                }
              </p>
              <dl>
                <dt>
                  {
                    currentTexts.revisors_handling_9
                      .revisors_handling_9_alt_2_beskrivelse.label
                  }{" "}
                  {
                    currentTexts.revisors_handling_9
                      .revisors_handling_9_alt_2_beskrivelse.sublabel
                  }
                </dt>
                <dd>{values.revisors_handling_9_alt_2_beskrivelse ?? "-"}</dd>
              </dl>
            </>
          );
        return (
          <p>
            <i>Svar mangler</i>
          </p>
        );
      },
    },
    revisors_handling_10: {
      title: "Handling 10",
      description: `
        Vi har kontrolleret, om oplysningen om meddelt dispensation 
        fra kravet om daglig bogføring og regulering, der fremgår 
        af punkt 1.4 og 1.5 i ${advokatensAdvokatselskabets()} egenerklæring,
        er i overensstemmelse med en modtaget skriftlig 
        dispensation.
      `,
      answer_heading: "Observation",
      revisors_handling_10_question: {
        id: "revisors_handling_10_question",
        revisors_handling_10_question_alt_1: {
          value: "revisors_handling_10_question_alt_1_fandt_alt_ok",
          labelPart1: `
            Vi fandt, at ${advokatensAdvokatselskabets()} oplysning 
            om at have modtaget skriftlig dispensation fra 
            kravet om daglig bogføring og regulering var i 
            overensstemmelse med dispensationen, jf. brev af `,
          revisors_handling_10_alt_1_dispensationDato: {
            id: "revisors_handling_10_alt_1_dispensationDato",
            getValue: () => {
              return values.revisors_handling_10_alt_1_dispensationDato ? (
                parseDateString(
                  values.revisors_handling_10_alt_1_dispensationDato,
                )
              ) : (
                <i>dato mangler</i>
              );
            },
          },
          labelPart2: ` fra Advokatsamfundets sekretariat og var
            gældende for perioden `,
          revisors_handling_10_alt_1_dispensationPeriodeDatoFra: {
            id: "revisors_handling_10_alt_1_dispensationPeriodeDatoFra",
            getValue: () => {
              return values.revisors_handling_10_alt_1_dispensationPeriodeDatoFra ? (
                parseDateString(
                  values.revisors_handling_10_alt_1_dispensationPeriodeDatoFra,
                )
              ) : (
                <i>dato mangler</i>
              );
            },
          },
          labelPart3: ` til `,
          revisors_handling_10_alt_1_dispensationPeriodeDatoTil: {
            id: "revisors_handling_10_alt_1_dispensationPeriodeDatoTil",
            getValue: () => {
              return values.revisors_handling_10_alt_1_dispensationPeriodeDatoTil ? (
                parseDateString(
                  values.revisors_handling_10_alt_1_dispensationPeriodeDatoTil,
                )
              ) : (
                <i>dato mangler</i>
              );
            },
          },
        },
        revisors_handling_10_question_alt_2: {
          value: "revisors_handling_10_question_alt_1_fandt_ikke_alt_ok",
          label: `
            Vi fandt, at ${advokatensAdvokatselskabets()} ikke havde 
            modtaget dispensation fra kravet om daglig bogføring, 
            som det fremgik af ${advokatensAdvokatselskabets()} 
            egenerklæring.
          `,
        },
      },
      revisors_handling_10_alt_2_uddybning: {
        id: "revisors_handling_10_alt_2_uddybning",
        label: "Uddybning",
      },
      getAnswer: () => {
        const currentTexts = texts();

        if (values.advokatens_section_1_4_dispensation_ja_nej === "Nej") {
          return (
            <p>
              Handling 10 er ikke relevant, da advokat ikke har angivet at der
              er givet dispensation.
            </p>
          );
        }

        if (
          values.revisors_handling_10_question ===
          currentTexts.revisors_handling_10.revisors_handling_10_question
            .revisors_handling_10_question_alt_1.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_10
                    .revisors_handling_10_question
                    .revisors_handling_10_question_alt_1.labelPart1
                }
                {currentTexts.revisors_handling_10.revisors_handling_10_question.revisors_handling_10_question_alt_1.revisors_handling_10_alt_1_dispensationDato.getValue()}
                {
                  currentTexts.revisors_handling_10
                    .revisors_handling_10_question
                    .revisors_handling_10_question_alt_1.labelPart2
                }
                {currentTexts.revisors_handling_10.revisors_handling_10_question.revisors_handling_10_question_alt_1.revisors_handling_10_alt_1_dispensationPeriodeDatoFra.getValue()}
                {
                  currentTexts.revisors_handling_10
                    .revisors_handling_10_question
                    .revisors_handling_10_question_alt_1.labelPart3
                }
                {currentTexts.revisors_handling_10.revisors_handling_10_question.revisors_handling_10_question_alt_1.revisors_handling_10_alt_1_dispensationPeriodeDatoTil.getValue()}
              </p>
            </>
          );

        if (
          values.revisors_handling_10_question ===
          currentTexts.revisors_handling_10.revisors_handling_10_question
            .revisors_handling_10_question_alt_2.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_10
                    .revisors_handling_10_question
                    .revisors_handling_10_question_alt_2.label
                }
              </p>
              <dl>
                <dt>
                  {
                    currentTexts.revisors_handling_10
                      .revisors_handling_10_alt_2_uddybning.label
                  }
                </dt>
                <dd>{values.revisors_handling_10_alt_2_uddybning ?? "-"}</dd>
              </dl>
            </>
          );

        return (
          <p>
            <i>Svar mangler</i>
          </p>
        );
      },
    },
    revisors_handling_11: {
      title: "Handling 11",
      description: (
        <>
          <p>Vi har udvalgt en tilfældig periode efter følgende kriterier:</p>
          <ul>
            <li>Perioden skal være en sammenhængende periode</li>
            <li>Perioden omfatter mindst 14 dage</li>
            <li>
              I perioden skal der være mindst 5 dage med bevægelser, der
              vedrører klientbogholderiet
            </li>
            <li>
              Hvis {advokatenAdvokatselskabet()} årligt har mindre end 5
              bevægelser, der vedrører klientbogholderiet, skal perioden omfatte
              hele året.
            </li>
          </ul>
          <p>
            For den udvalgte periode har vi kontrolleret, om{" "}
            {advokatenAdvokatselskabet()} har foretaget daglig bogføring på alle
            dage, hvor advokatvirksomheden var åben, og på dage, hvor der i
            øvrigt var foretaget økonomiske transaktioner fra
            advokatvirksomheden, eller som minimum i overensstemmelse med en
            eventuel dispensation.
          </p>
        </>
      ),
      answer_heading: "Observation",
      revisors_handling_11_periode_fra: {
        id: "revisors_handling_11_periode_fra",
      },
      revisors_handling_11_periode_til: {
        id: "revisors_handling_11_periode_til",
      },
      revisors_handling_11_periode: {
        label: "Udvalgt periode:",
        getText: () => {
          return (
            <>
              {values.revisors_handling_11_periode_fra ? (
                parseDateString(values.revisors_handling_11_periode_fra)
              ) : (
                <i>dato mangler</i>
              )}{" "}
              -{" "}
              {values.revisors_handling_11_periode_til ? (
                parseDateString(values.revisors_handling_11_periode_til)
              ) : (
                <i>dato mangler</i>
              )}
            </>
          );
        },
      },
      revisors_handling_11_question: {
        id: "revisors_handling_11_question",
        revisors_handling_11_question_alt_1: {
          value: "revisors_handling_11_question_alt_1_fandt_alt_ok",
          label: `
          For ovennævnte periode fandt vi, at ${advokatenAdvokatselskabet()}
          havde foretaget daglig bogføring på alle dage, hvor
          advokatvirksomheden var åben, og på dage, hvor der i øvrigt
          var foretaget økonomiske transaktioner fra advokatvirksomheden,
          eller som minimum i overensstemmelse med en eventuel dispensation.
          `,
        },
        revisors_handling_11_question_alt_2: {
          value: "revisors_handling_11_question_alt_2_fandt_ikke_alt_ok",
          label: `
          For ovennævnte periode fandt vi, at ${advokatenAdvokatselskabet()}
          ikke havde foretaget daglig bogføring på alle dage, hvor
          advokatvirksomheden var åben, eller på dage, hvor der i øvrigt
          var foretaget økonomiske transaktioner fra advokatvirksomheden,
          eller som minimum i overensstemmelse med en eventuel dispensation.
          `,
        },
      },
      revisors_handling_11_alt_2_uddybning: {
        id: "revisors_handling_11_alt_2_uddybning",
        label: "Uddybning",
        sublabel:
          "(hvilke dage var der ikke sket daglig bogføring, og begrundelse herfor):",
      },
      getAnswer: () => {
        const currentTexts = texts();
        if (
          values.revisors_handling_11_question ===
          currentTexts.revisors_handling_11.revisors_handling_11_question
            .revisors_handling_11_question_alt_1.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_11
                    .revisors_handling_11_question
                    .revisors_handling_11_question_alt_1.label
                }
              </p>
            </>
          );
        if (
          values.revisors_handling_11_question ===
          currentTexts.revisors_handling_11.revisors_handling_11_question
            .revisors_handling_11_question_alt_2.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_11
                    .revisors_handling_11_question
                    .revisors_handling_11_question_alt_2.label
                }
              </p>
              <dl>
                <dt>
                  {
                    currentTexts.revisors_handling_11
                      .revisors_handling_11_alt_2_uddybning.label
                  }
                </dt>
                <dd>{values.revisors_handling_11_alt_2_uddybning ?? "-"}</dd>
              </dl>
            </>
          );
        return (
          <p>
            <i>Svar mangler</i>
          </p>
        );
      },
    },
    revisors_handling_12: {
      title: "Handling 12",
      description: `
        Vi har for 5 (eller alle, hvis der er mindre end 
        5 dage) tilfældigt udvalgte dage, hvor 
        advokatvirksomheden var åben, og på dage, hvor 
        der i øvrigt var foretaget økonomiske 
        transaktioner fra advokatvirksomheden, 
        og hvor der var bevægelser, som vedrører 
        klienttilsvaret, kontrolleret, om 
        ${advokatenAdvokatselskabet()} foretog afstemning og 
        regulering af det samlede klienttilsvar.
      `,
      answer_description:
        "Positiv difference = penge bogføres ind på klientkontoen, Negativ difference = penge bogføres ud af klientkontoen",
      answer_heading: "Observation",
      revisors_handling_12_table: {
        id: "revisors_handling_12_table",
        revisors_handling_12_table_column_dato: {
          id: "revisors_handling_12_table_columns_dato",
          label: "Dato",
        },
        revisors_handling_12_table_column_samlet_tilsvar_input: {
          inputId: "revisors_handling_12_table_columns_samlet_tilsvar",
          beloebId: "revisors_handling_12_table_columns_samlet_tilsvar",
          label: "Samlet klienttilsvar indestående i pengeinstituttet",
        },
        revisors_handling_12_table_column_reguleringsbeloeb_input: {
          inputId: "revisors_handling_12_table_columns_reguleringsbeloeb_input",
          beloebId:
            "revisors_handling_12_table_columns_reguleringsbeloeb_beloeb",
          label:
            "Difference mellem indestående i pengeinstitut og det bogførte tilsvar",
          getValue: (beloeb, plusMinus) => {
            return getBeloebTextWithPlusMinus(beloeb, plusMinus);
          },
        },
        revisors_handling_12_table_column_reguleringsbeloeb_plus_minus: {
          id: "revisors_handling_12_table_column_reguleringsbeloeb_plus_minus",
        },
        revisors_handling_12_table_column_reguleringsdato: {
          id: "revisors_handling_12_table_columns_reguleringsdato",
          label: "Reguleringsdato",
        },
        revisors_handling_12_table_column_mellemvaerende_input: {
          inputId: "revisors_handling_12_table_columns_mellemvaerende_input",
          beloebId: "revisors_handling_12_table_columns_mellemvaerende_beloeb",
          label: "Faktisk regulering",
        },
      },
      revisors_handling_12_question: {
        id: "revisors_handling_12_question",
        revisors_handling_12_question_alt_1: {
          value: "revisors_handling_12_question_alt_1_fandt_alt_ok",
          label: `
            Vi fandt, at ${advokatenAdvokatselskabet()} på de udvalgte 
            dage havde foretaget afstemning og hvis nødvendigt 
            regulering af det samlede klienttilsvar.
          `,
        },
        revisors_handling_12_question_alt_2: {
          value: "revisors_handling_12_question_alt_2_fandt_ikke_alt_ok",
          label: `
            Vi fandt, at ${advokatenAdvokatselskabet()} på de udvalgte dage 
            ikke havde foretaget afstemning eller hvis nødvendigt 
            regulering af det samlede klienttilsvar.
          `,
        },
      },
      revisors_handling_12_alt_2_uddybning: {
        id: "revisors_handling_12_alt_2_uddybning",
        label: "Uddybning",
        sublabel:
          "(hvilke dage var der ikke foretaget afstemning/rettidig regulering, og eventuel begrundelse herfor):",
      },
      getAnswer: () => {
        const currentTexts = texts();
        if (
          values.revisors_handling_12_question ===
          currentTexts.revisors_handling_12.revisors_handling_12_question
            .revisors_handling_12_question_alt_1.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_12
                    .revisors_handling_12_question
                    .revisors_handling_12_question_alt_1.label
                }
              </p>
            </>
          );
        if (
          values.revisors_handling_12_question ===
          currentTexts.revisors_handling_12.revisors_handling_12_question
            .revisors_handling_12_question_alt_2.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_12
                    .revisors_handling_12_question
                    .revisors_handling_12_question_alt_2.label
                }
              </p>
              <dl>
                <dt>
                  {
                    currentTexts.revisors_handling_12
                      .revisors_handling_12_alt_2_uddybning.label
                  }{" "}
                  {
                    currentTexts.revisors_handling_12
                      .revisors_handling_12_alt_2_uddybning.sublabel
                  }
                </dt>
                <dd>{values.revisors_handling_12_alt_2_uddybning ?? "-"}</dd>
              </dl>
            </>
          );
        return (
          <p>
            <i>Svar mangler</i>
          </p>
        );
      },
    },
    revisors_handling_13: {
      title: "Handling 13",
      description: (
        <>
          <p>Vi har udvalgt en stikprøve efter følgende kriterier:</p>
          <ul>
            <li>
              Stikprøven skal omfatte samtlige transaktioner på de dage, der
              blev <strong>udvalgt i handling 12</strong>, dog gælder følgende:
            </li>
            <li>
              Hvis der er mere end 5 transaktioner pr dag på én eller flere af
              de udvalgte dage, udvælges der på de pågældende dage 5 tilfældige
              transaktioner.
            </li>
          </ul>
          <p>
            For den udvalgte stikprøve har vi kontrolleret, om der var
            overensstemmelse mellem de bogførte transaktioner og de
            underliggende bilag.
          </p>
        </>
      ),
      answer_heading: "Observation",
      revisors_handling_13_antal_transaktioner_udvalgt_som_stikproeve: {
        id: "revisors_handling_13_antal_transaktioner_udvalgt_som_stikproeve",
        label: "Antal transaktioner udvalgt som stikprøve (mellem 0 og 25):",
      },
      revisors_handling_13_question: {
        id: "revisors_handling_13_question",
        revisors_handling_13_question_alt_1: {
          value: "revisors_handling_13_question_alt_1_fandt_alt_ok",
          label: `
            Vi fandt, at der for den udvalgte stikprøve var overensstemmelse 
            mellem de bogførte transaktioner og de underliggende bilag.
          `,
        },
        revisors_handling_13_question_alt_2: {
          value: "revisors_handling_13_question_alt_2_fandt_ikke_alt_ok",
          label: `
            Vi fandt, at der for den udvalgte stikprøve ikke var overensstemmelse 
            mellem de bogførte transaktioner og de underliggende bilag.
          `,
        },
      },
      revisors_handling_13_question_alt_2_uddybning_table: {
        id: "revisors_handling_13_question_alt_2_uddybning_table",
        label: "Uddybning:",
        revisors_handling_13_question_alt_2_uddybning_table_column_dato: {
          id: "revisors_handling_13_question_alt_2_uddybning_table_column_dato",
          label: "Dato",
        },
        revisors_handling_13_question_alt_2_uddybning_table_column_bilagsnummer:
          {
            id: "revisors_handling_13_question_alt_2_uddybning_table_column_bilagsnummer",
            label: "Bilagsnummer",
          },
        revisors_handling_13_question_alt_2_uddybning_table_column_beloeb: {
          id: "revisors_handling_13_question_alt_2_uddybning_table_column_beloeb",
          label: "Beløb",
        },
        revisors_handling_13_question_alt_2_uddybning_table_column_begrundelse:
          {
            id: "revisors_handling_13_question_alt_2_uddybning_table_column_begrundelse",
            label: "Begrundelse",
          },
      },
    },
    revisors_handling_14: {
      title: "Handling 14",
      description: `
        Vi har undersøgt, om der foreligger dokumentation 
        for afstemning mellem det samlede klienttilsvar og 
        summen af de enkelte mellemværender mindst én gang 
        hver måned.`,
      answer_heading: "Observation",
      revisors_handling_14_question: {
        id: "revisors_handling_14_question",
        revisors_handling_14_question_alt_1: {
          value: "revisors_handling_14_question_alt_1_fandt_alt_ok",
          label: `
              Vi fandt, at der forelå dokumentation for afstemning 
              mellem det samlede klienttilsvar og summen af de 
              enkelte mellemværender mindst én gang hver måned.
            `,
        },
        revisors_handling_14_question_alt_2: {
          value: "revisors_handling_14_question_alt_2_fandt_ikke_alt_ok",
          label: `
              Vi fandt, at der ikke forelå dokumentation for afstemning 
              mellem det samlede klienttilsvar og summen af de 
              enkelte mellemværender mindst én gang om måneden.
            `,
        },
      },
      revisors_handling_14_alt_2_uddybning: {
        id: "revisors_handling_14_alt_2_uddybning",
        label: "Uddybning",
        sublabel:
          "(i hvor mange tilfælde var der ikke dokumentation for afstemning, og begrundelse herfor):",
      },
      getAnswer: () => {
        const currentTexts = texts();
        if (
          values.revisors_handling_14_question ===
          currentTexts.revisors_handling_14.revisors_handling_14_question
            .revisors_handling_14_question_alt_1.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_14
                    .revisors_handling_14_question
                    .revisors_handling_14_question_alt_1.label
                }
              </p>
            </>
          );
        if (
          values.revisors_handling_14_question ===
          currentTexts.revisors_handling_14.revisors_handling_14_question
            .revisors_handling_14_question_alt_2.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_14
                    .revisors_handling_14_question
                    .revisors_handling_14_question_alt_2.label
                }
              </p>
              <dl>
                <dt>
                  {
                    currentTexts.revisors_handling_14
                      .revisors_handling_14_alt_2_uddybning.label
                  }{" "}
                  {
                    currentTexts.revisors_handling_14
                      .revisors_handling_14_alt_2_uddybning.sublabel
                  }
                </dt>
                <dd>{values.revisors_handling_14_alt_2_uddybning ?? "-"}</dd>
              </dl>
            </>
          );
        return (
          <p>
            <i>Svar mangler</i>
          </p>
        );
      },
    },
    revisors_handling_15: {
      title: "Handling 15",
      description: `
        Vi har udvalgt en afstemning for hver måned, hvor ${advokatenAdvokatselskabet()}
        har foretaget afstemning, jf. handling 14, og undersøgt, om der var 
        overensstemmelse mellem det samlede klienttilsvar og summen af de 
        enkelte mellemværender.`,
      answer_heading: "Observation",
      revisors_handling_15_question: {
        id: "revisors_handling_15_question",
        revisors_handling_15_question_alt_1: {
          value: "revisors_handling_15_question_alt_1_fandt_alt_ok",
          label: `
            Vi fandt, at der på de udvalgte afstemninger var overensstemmelse 
            mellem det samlede klienttilsvar og summen af de enkelte 
            mellemværender.
          `,
        },
        revisors_handling_15_question_alt_2: {
          value: "revisors_handling_15_question_alt_2_fandt_ikke_alt_ok",
          label: `
            Vi fandt, at der på de udvalgte afstemninger ikke var overensstemmelse 
            mellem det samlede klienttilsvar og summen af de enkelte mellemværender.
          `,
        },
      },
      revisors_handling_15_alt_2_uddybning: {
        id: "revisors_handling_15_alt_2_uddybning",
        label: "Uddybning",
        sublabel: "(forklaring på differencen)",
      },
      getAnswer: () => {
        const currentTexts = texts();
        if (
          values.revisors_handling_15_question ===
          currentTexts.revisors_handling_15.revisors_handling_15_question
            .revisors_handling_15_question_alt_1.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_15
                    .revisors_handling_15_question
                    .revisors_handling_15_question_alt_1.label
                }
              </p>
            </>
          );
        if (
          values.revisors_handling_15_question ===
          currentTexts.revisors_handling_15.revisors_handling_15_question
            .revisors_handling_15_question_alt_2.value
        )
          return (
            <>
              <p>
                {
                  currentTexts.revisors_handling_15
                    .revisors_handling_15_question
                    .revisors_handling_15_question_alt_2.label
                }
              </p>
              <dl>
                <dt>
                  {
                    currentTexts.revisors_handling_15
                      .revisors_handling_15_alt_2_uddybning.label
                  }{" "}
                  {
                    currentTexts.revisors_handling_15
                      .revisors_handling_15_alt_2_uddybning.sublabel
                  }
                </dt>
                <dd>{values.revisors_handling_15_alt_2_uddybning ?? "-"}</dd>
              </dl>
            </>
          );
        return (
          <p>
            <i>Svar mangler</i>
          </p>
        );
      },
    },
    revisors_handling_16: {
      title: "Handling 16",
      description: (
        <>
          <p>Vi har udvalgt en stikprøve efter følgende kriterier:</p>
          <ul>
            <li>
              Stikprøven skal omfatte kreditnotaer udstedt i{" "}
              {new Date(slutDato).getFullYear()}{" "}
              <strong>
                i sager hvor der var klienttilsvar på tidspunktet for fakturaens
                udstedelse
              </strong>
            </li>
            <li>
              Der udvælges 5 måneder (stikprøveperioden), hvori der for hver
              måned udtages 5 kreditnotaer
            </li>
            <li>
              Hvis der ikke kan udtages 5 kreditnotaer pr. måned for
              stikprøveperioden, udtages i alt 25 kreditnotaer fordelt over
              stikprøveperioden
            </li>
            <li>
              Hvis der fortsat ikke kan udtages i alt 25 kreditnotaer, udvides
              stikprøveperioden med én tilfældigt udvalgt måned, indtil der
              enten er udtaget 25 kreditnotaer i alt, eller til alle årets
              kreditnotaer er udtaget.
            </li>
          </ul>
          <p>
            For den udvalgte stikprøve har vi undersøgt om der er udstedt
            kreditnotaer, som ikke udelukkende skyldes enten fejl i
            modtageradresse eller fejl i intern salærfordeling.
          </p>
        </>
      ),
      answer_heading: "Observation",
      revisors_handling_16_antal_kreditnotaer_udvalgt_som_stikproeve: {
        id: "revisors_handling_16_antal_kreditnotaer_udvalgt_som_stikproeve",
        label: "Antal kreditnotaer udvalgt som stikprøve (mellem 0 og 25):",
      },
      revisors_handling_16_question: {
        id: "revisors_handling_16_question",
        revisors_handling_16_question_alt_1: {
          value: "revisors_handling_16_question_alt_1_fandt_alt_ok",
          label: `
            For den udvalgte stikprøve fandt vi, at der ikke var udstedt 
            kreditnotaer i sager med klienttilsvar af andre årsager end 
            fejl i modtageradresse eller fejl i interne salærfordelinger.
          `,
        },
        revisors_handling_16_question_alt_2: {
          value: "revisors_handling_16_question_alt_2_fandt_ikke_alt_ok",
          label: `
            For den udvalgte stikprøve fandt vi, at der var udstedt 
            kreditnotaer i sager med klienttilsvar af andre årsager 
            end fejl i modtageradresse eller fejl i intern salærfordeling.
          `,
        },
      },
      revisors_handling_16_question_alt_2_uddybning_table: {
        id: "revisors_handling_16_question_alt_2_uddybning_table",
        label: "",
        revisors_handling_16_question_alt_2_uddybning_table_column_fakturanr: {
          id: "revisors_handling_16_question_alt_2_uddybning_table_column_fakturanr",
          label: "Fakturanummer",
        },
        revisors_handling_16_question_alt_2_uddybning_table_column_fakturadato:
          {
            id: "revisors_handling_16_question_alt_2_uddybning_table_column_fakturadato",
            label: "Dato for faktura",
          },
        revisors_handling_16_question_alt_2_uddybning_table_column_fakturabeloeb:
          {
            id: "revisors_handling_16_question_alt_2_uddybning_table_column_fakturabeloeb",
            label: "Fakturabeløb",
          },
        revisors_handling_16_question_alt_2_uddybning_table_column_modtager_navn_cvr:
          {
            id: "revisors_handling_16_question_alt_2_uddybning_table_column_modtager_navn_cvr",
            label: "Navn på fakturamodtager, plus evt. CVR-nr.",
          },
        revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_nr:
          {
            id: "revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_nr",
            label: "Kreditnotanummer",
          },
        revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_beloeb:
          {
            id: "revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_beloeb",
            label: "Kreditnotabeløb",
          },
        revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_begrundelse:
          {
            id: "revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_begrundelse",
            label: "Advokatens begrundelse for kreditnota",
          },
        revisors_handling_16_question_alt_2_uddybning_table_column_ny_faktura_modtager_navn_cvr:
          {
            id: "revisors_handling_16_question_alt_2_uddybning_table_column_ny_faktura_modtager_navn_cvr",
            label: "Navn på modtager af eventuel ny faktura, plus evt. CVR-nr.",
          },
        revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_dato:
          {
            id: "revisors_handling_16_question_alt_2_uddybning_table_column_kreditnota_dato",
            label: "Dato på kreditnota",
          },
        revisors_handling_16_question_alt_2_uddybning_table_column_ny_faktura_beloeb:
          {
            id: "revisors_handling_16_question_alt_2_uddybning_table_column_ny_faktura_beloeb",
            label: "Beløb på eventuel ny faktura",
          },
      },
    },
    revisors_oevrige_oplysninger: {
      title: "Øvrige oplysninger",
      revisors_oevrige_oplysninger_kundeforhold_accepteret_dato: {
        id: "revisors_oevrige_oplysninger_kundeforhold_accepteret_dato",
        label: `
        Vi kan oplyse, at vi accepterede kundeforholdet med ${advokatenAdvokatselskabet()} pr.
        `,
      },
      checkboxes_description:
        "Vores arbejde for advokaten/advokatselskabet omfatter foruden afgivelse af nærværende erklæring:",
      revisors_oevrige_oplysninger_arbejsdomraade_checkbox_revision_af_aarsregnskab:
        {
          id: "revisors_oevrige_oplysninger_arbejsdomraade_checkbox_revision_af_aarsregnskab",
          label: "Revision af årsregnskab",
        },
      revisors_oevrige_oplysninger_arbejsdomraade_checkbox_udvidet_gennemgang_af_aarsregnskab:
        {
          id: "revisors_oevrige_oplysninger_arbejsdomraade_checkbox_udvidet_gennemgang_af_aarsregnskab",
          label: "Udvidet gennemgang af årsregnskab",
        },
      revisors_oevrige_oplysninger_arbejsdomraade_checkbox_review_af_aarsregnskab:
        {
          id: "revisors_oevrige_oplysninger_arbejsdomraade_checkbox_review_af_aarsregnskab",
          label: "Review af årsregnskab",
        },
      revisors_oevrige_oplysninger_arbejsdomraade_checkbox_assistance_med_regnskabsopslilling_af_aarsregnskab:
        {
          id: "revisors_oevrige_oplysninger_arbejsdomraade_checkbox_assistance_med_regnskabsopslilling_af_aarsregnskab",
          label: "Assistance med regnskabsopstilling af årsregnskab",
        },
      revisors_oevrige_oplysninger_arbejsdomraade_checkbox_bogfoering: {
        id: "revisors_oevrige_oplysninger_arbejsdomraade_checkbox_bogfoering",
        label: "Bogføring",
      },
      revisors_oevrige_oplysninger_revisors_navn: {
        id: "revisors_oevrige_oplysninger_revisors_navn",
        label: "Revisors navn",
      },
      revisors_oevrige_oplysninger_revisor_type: {
        id: "revisors_oevrige_oplysninger_revisor_type",
        label: "Revisortype",
        revisors_oevrige_oplysninger_revisor_type_option_statsautoriseret_revisor:
          {
            id: "revisors_oevrige_oplysninger_revisor_type_option_statsautoriseret_revisor",
            label: "Statsautoriseret revisor",
          },
        revisors_oevrige_oplysninger_revisor_type_option_registreret_revisor: {
          id: "revisors_oevrige_oplysninger_revisor_type_option_registreret_revisor",
          label: "Registreret revisor",
        },
      },
      revisors_oevrige_oplysninger_firmanavn: {
        id: "revisors_oevrige_oplysninger_firmanavn",
        label: "Firmanavn",
      },
      revisors_oevrige_oplysninger_cvrnr: {
        id: "revisors_oevrige_oplysninger_cvrnr",
        label: "CVR-nummer",
      },
      revisors_oevrige_oplysninger_adr_vej_husnr: {
        id: "revisors_oevrige_oplysninger_adr_vej_husnr",
        label: "Adresse",
      },
      revisors_oevrige_oplysninger_adr_postnr_by: {
        id: "revisors_oevrige_oplysninger_adr_postnr_by",
      },
    },
  };

  return response;
};

/**
 * To be used for static texts like ids and labels that do not change
 * depending on the values of the form and metadata
 */
export const staticTexts = getTexts(KKE_FORM_INITIAL_VALUES, {
  startDato: "1900-01-01",
  slutDato: "1900-12-31",
  fristDato: "1901-03-31",
});

function getBeloebTextWithPlusMinus(
  beloeb: number | undefined,
  plusMinus: string | undefined,
) {
  if (beloeb === undefined) return "-";
  const value = plusMinus === "-" ? -beloeb : beloeb;
  return (
    value.toLocaleString("da-DK", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }) + " kr"
  );
}

function getDisplayCurrency(
  amount: number | undefined,
  currency: string | undefined = "kr",
) {
  if (!amount) return "-";
  return `${amount
    .toLocaleString("da-DK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .padEnd(2, "0")} ${currency}`;
}
